import SidePanel from '@monofront/design-system/organisms/SidePanel/SidePanel';
import { DeliveryBarMachineContext } from '@monofront/business/contexts/DeliveryBarContext';
import { DefaultPanelHeader } from '@features/DeliveryBar/Panels/DefaultPanelHeader';
import getAddressGeolocation from '@monofront/business/utils/mapbox';
import logger from '@utils/logger';
import { useEffect, useRef, useState } from 'react';
import { machineSelectedAddressSelector } from '@monofront/business/stateMachines/deliveryBar/selectors';
import getConfig from 'next/config';
import {
  IPickupPoint,
  ListEligibleShippingMethodsQueryResult,
  ShippingMethodTypes,
} from '@monofront/types';
import {
  GET_DROP_OFF_POINTS,
  LIST_ELIGIBLE_SHIPPING_METHODS,
} from '@monofront/business/queries';
import { useQuery } from '@apollo/client';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import PickupMap from '@features/CheckoutSelectShippingMethod/SelectPickup/PickupMap';
import { useTranslations } from 'next-intl';
import {
  AddressBlock,
  PickupPointSelectionBlock,
  PickupPointSelector,
} from 'src/components/PickupPointSelector';
import { SHOW_MORE_PICKUPS_STEP_SIZE } from '@monofront/business/constants';
import { EVENTS } from '@monofront/business/stateMachines/deliveryBar/events';

const {
  publicRuntimeConfig: { mapboxToken },
} = getConfig();

type PickUpNeededPanelProps = {
  closePanel: () => void;
};

export const PickUpNeededPanel: React.FC<PickUpNeededPanelProps> = ({
  closePanel,
}) => {
  const tShipping = useTranslations('Shipping');
  const tGlobal = useTranslations('Global');

  const [homePosition, setHomePosition] = useState<{
    longitude: number;
    latitude: number;
  } | null>(null);
  const [pickupFocused, setPickupFocused] = useState<string | null>(null);
  const [displayedPickupsCount, setDisplayedPickupsCount] = useState<number>(
    SHOW_MORE_PICKUPS_STEP_SIZE,
  );

  const pickupElementsRef = useRef({});

  const { send } = DeliveryBarMachineContext.useActorRef();
  const selectedAddress = DeliveryBarMachineContext.useSelector(
    machineSelectedAddressSelector,
  );

  const {
    data: eligibleShippingMethods,
    loading: isEligibleShippingMethodsLoading,
  } = useQuery<ListEligibleShippingMethodsQueryResult>(
    LIST_ELIGIBLE_SHIPPING_METHODS,
    {
      ssr: false,
      variables: {
        shippingAddress: {
          countryCode: selectedAddress?.country,
          postcode: selectedAddress?.postalCode,
        },
      },
      skip: !selectedAddress,
    },
  );

  const { data: pickupPointsList, loading: isPickupPointsListLoading } =
    useQuery<{
      getDropOffPoints: IPickupPoint[];
    }>(GET_DROP_OFF_POINTS, {
      ssr: false,
      skip:
        !selectedAddress ||
        !eligibleShippingMethods?.listEligibleShippingMethods ||
        !homePosition,
      variables: {
        city: selectedAddress?.city,
        zip: selectedAddress?.postalCode,
        country: selectedAddress?.country,
        limit: 20,
        couriers: eligibleShippingMethods?.listEligibleShippingMethods
          .filter((method) => method.type === ShippingMethodTypes.PICKUP)
          .map((method) => method.tmsCode),
        lat: homePosition?.latitude,
        lng: homePosition?.longitude,
      },
    });

  useEffect(() => {
    if (!selectedAddress) return;
    const getCoords = async () => {
      const [longitude, latitude] = await getAddressGeolocation(
        {
          mapboxToken,
          address: `${selectedAddress.street} ${selectedAddress.postalCode} ${selectedAddress.city}`,
          countryCode: selectedAddress.country,
        },
        logger,
      );

      setHomePosition({ longitude, latitude });
    };

    getCoords();
  }, [selectedAddress]);

  useEffect(() => {
    if (pickupFocused) {
      pickupElementsRef.current?.[pickupFocused]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [pickupFocused]);

  const handlePickupPointSelection = (pickupPoint: IPickupPoint) => {
    send({
      type: EVENTS.PICKUP_POINT_SELECT,
      pickupPoint: {
        type: ShippingMethodTypes.PICKUP,
        pickupPointId: pickupPoint.pointId,
        pickupPointLabel: pickupPoint.pointName,
        pickupPointCity: pickupPoint.city,
        pickupPointStreet: pickupPoint.street,
        pickupPointZipCode: pickupPoint.zip,
      },
    });
  };

  const pickupPoints = pickupPointsList?.getDropOffPoints || [];
  const seeMoreCount = Math.min(
    pickupPoints.length - displayedPickupsCount,
    SHOW_MORE_PICKUPS_STEP_SIZE,
  );

  const displayedPickupList = pickupPoints.slice(0, displayedPickupsCount);

  return (
    <>
      <DefaultPanelHeader
        onClose={closePanel}
        onGoBack={() =>
          send({
            type: EVENTS.GO_TO_STATE_SHIPPING_METHOD_SELECTOR,
          })
        }
      />
      <SidePanel.SidePanelBody
        dataTestId="Deliverybar-pickup-infos"
        UNSAFE_className="!tw-pl-4 !tw-pr-0 !tw-pt-6 !tw-pb-0 tw-flex tw-flex-col tw-h-full"
      >
        {isEligibleShippingMethodsLoading || isPickupPointsListLoading ? (
          <HeartLoader />
        ) : (
          <>
            {!!(pickupPointsList?.getDropOffPoints && homePosition) && (
              <>
                <div className="map tw-h-64 tw-min-h-64 tw-pr-4">
                  <PickupMap
                    pickupList={displayedPickupList}
                    pickupFocused={pickupFocused}
                    setPickupFocused={setPickupFocused}
                    homePosition={homePosition}
                  />
                </div>

                <div className="tw-mt-3 tw-min-h-0 tw-flex-grow tw-overflow-y-auto tw-pb-6 tw-pr-4">
                  <ol
                    className="tw-list-none"
                    data-testId="Deliverybar-pickup-list"
                  >
                    {displayedPickupList.map((pickupPoint, index) => {
                      const {
                        dayOfWeek,
                        pointId,
                        pointName,
                        street,
                        zip,
                        city,
                        distanceFromOrigin,
                      } = pickupPoint;

                      return (
                        <li
                          key={pointId}
                          className="tw-mb-2"
                          onClick={() => setPickupFocused(pointId)}
                          ref={(markerRef) => {
                            pickupElementsRef.current[pointId] = markerRef;
                          }}
                        >
                          <PickupPointSelector
                            isSelected={pickupFocused === pointId}
                          >
                            <AddressBlock
                              index={index + 1}
                              pickupPointName={pointName}
                              address={{ street, postCode: zip, city }}
                              distanceFromRefPoint={distanceFromOrigin.value}
                            />
                            <PickupPointSelectionBlock
                              buttonLabel={tShipping('choose')}
                              onSelect={() =>
                                handlePickupPointSelection(pickupPoint)
                              }
                              expandLabel={tShipping('timeSlots')}
                              openingHours={[...dayOfWeek].sort(
                                (a, b) => a.day - b.day,
                              )}
                            />
                          </PickupPointSelector>
                        </li>
                      );
                    })}
                  </ol>
                  {seeMoreCount > 0 && (
                    <button
                      className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-mx-auto tw-mt-4"
                      onClick={() =>
                        setDisplayedPickupsCount(
                          (prev) => prev + SHOW_MORE_PICKUPS_STEP_SIZE,
                        )
                      }
                    >{`${tGlobal('seeMore')} (${seeMoreCount})`}</button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </SidePanel.SidePanelBody>
      <style jsx>{`
        ol,
        .map {
          counter-reset: drop-off-counter;
        }
        ol li,
        :global(.marker-icon) {
          counter-increment: drop-off-counter;
        }
        :global(.marker-icon)::before {
          --size: 28px;
          border: 2px solid var(--white);
        }
        :global(.marker-icon)::before {
          display: block;
          content: counter(drop-off-counter);
          font-weight: bold;
          line-height: var(--size);
          width: var(--size);
          height: var(--size);
          background-color: var(--clr-primary-1);
          border-radius: 50%;
          text-align: center;
          margin-right: var(--space-4);
        }
      `}</style>
    </>
  );
};
